import { createSiteRegistry, type SeriesSite } from '@se-freshman-digital-2026/shared/sites'

import aiApplicationImage from '../assets/project-awakening/ai-application.webp'
import campusServiceImage from '../assets/project-awakening/campus-service.webp'
import dataVisualizationImage from '../assets/project-awakening/data-visualization.webp'
import interactiveWorkImage from '../assets/project-awakening/interactive-work.webp'
import mobileAppImage from '../assets/project-awakening/mobile-app.webp'
import teamEngineeringImage from '../assets/project-awakening/team-engineering.webp'

export interface SoftwareStage {
  id: string
  label: string
  english: string
  description: string
  output: string
}

export interface GrowthYear {
  year: string
  code: string
  theme: string
  summary: string
  skills: readonly string[]
  advisory: true
}

export interface CareerPath {
  id: string
  name: string
  english: string
  description: string
  signal: string
  color: string
}

export interface ProjectConcept {
  name: string
  type: string
  description: string
  pattern: 'network' | 'dashboard' | 'mobile' | 'ai' | 'game' | 'system'
  image: string
  imageAlt: string
  imagePosition: string
  imageScale: number
  accent: string
  motion: 'route' | 'trend' | 'touch' | 'inference' | 'particle' | 'merge'
  isConcept: true
}

export const softwareStages: readonly SoftwareStage[] = [
  {
    id: 'idea',
    label: '想法',
    english: 'IDEA',
    description: '从一个真实问题开始，先问清楚为什么值得做。',
    output: '问题假设',
  },
  {
    id: 'requirement',
    label: '需求',
    english: 'REQUIRE',
    description: '把模糊愿望翻译成可讨论、可验收的目标。',
    output: '需求边界',
  },
  {
    id: 'design',
    label: '设计',
    english: 'DESIGN',
    description: '让信息、界面与系统结构在动手前彼此对齐。',
    output: '系统蓝图',
  },
  {
    id: 'code',
    label: '编码',
    english: 'BUILD',
    description: '用清晰的模块和协作规则，把蓝图变成软件。',
    output: '可运行版本',
  },
  {
    id: 'test',
    label: '测试',
    english: 'VERIFY',
    description: '主动寻找边界与失败，让问题留在上线之前。',
    output: '质量证据',
  },
  {
    id: 'deploy',
    label: '部署',
    english: 'SHIP',
    description: '把版本稳定地交付给真实环境和真实用户。',
    output: '在线服务',
  },
  {
    id: 'operate',
    label: '运维',
    english: 'OPERATE',
    description: '观察运行状态，恢复故障，守住可靠与安全。',
    output: '稳定运行',
  },
  {
    id: 'iterate',
    label: '迭代',
    english: 'EVOLVE',
    description: '从反馈中学习，让下一次构建比这一次更准确。',
    output: '新一轮问题',
  },
]

export const growthYears: readonly GrowthYear[] = [
  {
    year: '大一',
    code: '01 / IGNITE',
    theme: '点亮计算思维',
    summary: '先不追赶工具，建立拆解问题、表达逻辑和持续练习的节奏。',
    skills: ['编程启蒙', '计算思维', '学习方法', '小作品'],
    advisory: true,
  },
  {
    year: '大二',
    code: '02 / CONNECT',
    theme: '连接数据与系统',
    summary: '从单个程序走向结构化数据、对象协作与可被访问的 Web。',
    skills: ['数据结构', '面向对象', '数据库', 'Web 基础'],
    advisory: true,
  },
  {
    year: '大三',
    code: '03 / SCALE',
    theme: '理解复杂系统',
    summary: '开始关心并发、网络、协作边界，以及团队如何交付可靠软件。',
    skills: ['操作系统', '计算机网络', '软件工程', '团队项目'],
    advisory: true,
  },
  {
    year: '大四',
    code: '04 / LAUNCH',
    theme: '把能力交给真实世界',
    summary: '用实习、毕业设计或研究方向，完成一次独立而完整的出发。',
    skills: ['实习实践', '毕业设计', '求职准备', '深造探索'],
    advisory: true,
  },
]

export const careerPaths: readonly CareerPath[] = [
  {
    id: 'frontend',
    name: '前端开发',
    english: 'FRONTEND',
    description: '把信息、动效与交互变成用户真正触摸到的体验。',
    signal: '体验界面',
    color: '#32e6ff',
  },
  {
    id: 'backend',
    name: '后端开发',
    english: 'BACKEND',
    description: '设计服务、数据和规则，让系统在看不见的地方可靠运转。',
    signal: '服务系统',
    color: '#2187ff',
  },
  {
    id: 'test',
    name: '测试开发',
    english: 'QUALITY',
    description: '用自动化、工具和工程判断，持续证明软件值得信任。',
    signal: '质量工程',
    color: '#5bd6b2',
  },
  {
    id: 'client',
    name: '客户端开发',
    english: 'CLIENT',
    description: '在手机、桌面或多端设备上构建流畅、稳定的原生体验。',
    signal: '终端体验',
    color: '#7aa5ff',
  },
  {
    id: 'data',
    name: '数据工程',
    english: 'DATA',
    description: '让数据被采集、组织、流动，并能支持清晰的判断。',
    signal: '数据管线',
    color: '#8c6cff',
  },
  {
    id: 'ai',
    name: '人工智能应用',
    english: 'AI APPLIED',
    description: '把模型能力接入具体问题，关注效果、边界与真实价值。',
    signal: '智能应用',
    color: '#bd77ff',
  },
  {
    id: 'product',
    name: '产品与项目管理',
    english: 'PRODUCT',
    description: '连接用户、目标与团队，让复杂协作始终朝同一方向前进。',
    signal: '协作交付',
    color: '#4de0c1',
  },
]

export const projectConcepts: readonly ProjectConcept[] = [
  {
    name: '校园服务平台',
    type: 'SERVICE / WEB',
    description: '把分散流程变成清楚、可达的一站式服务。',
    pattern: 'network',
    image: campusServiceImage,
    imageAlt: '校园建筑与服务节点通过路线汇聚到中央平台',
    imagePosition: '50% 52%',
    imageScale: 1.02,
    accent: '#32e6ff',
    motion: 'route',
    isConcept: true,
  },
  {
    name: '数据可视化',
    type: 'DATA / VISUAL',
    description: '让复杂数据拥有可以被理解的形状。',
    pattern: 'dashboard',
    image: dataVisualizationImage,
    imageAlt: '数据流经过处理系统后形成趋势图、柱状图和散点图',
    imagePosition: '54% 50%',
    imageScale: 1.025,
    accent: '#2187ff',
    motion: 'trend',
    isConcept: true,
  },
  {
    name: '移动应用',
    type: 'MOBILE / CLIENT',
    description: '围绕一个高频场景，打磨真正顺手的体验。',
    pattern: 'mobile',
    image: mobileAppImage,
    imageAlt: '移动设备中的多层页面响应触摸操作并完成状态切换',
    imagePosition: '48% 50%',
    imageScale: 1.02,
    accent: '#32e6ff',
    motion: 'touch',
    isConcept: true,
  },
  {
    name: 'AI 应用',
    type: 'MODEL / PRODUCT',
    description: '把模型接入清晰任务，而不是停在演示。',
    pattern: 'ai',
    image: aiApplicationImage,
    imageAlt: '文档、图像和音频输入经过多阶段推理后生成结构化结果',
    imagePosition: '52% 51%',
    imageScale: 1.015,
    accent: '#8c6cff',
    motion: 'inference',
    isConcept: true,
  },
  {
    name: '互动作品',
    type: 'PLAY / CREATIVE',
    description: '用代码、声音与图形创造可参与的表达。',
    pattern: 'game',
    image: interactiveWorkImage,
    imageAlt: '指针输入推动粒子网格产生实时形变与反馈',
    imagePosition: '49% 54%',
    imageScale: 1.025,
    accent: '#8c6cff',
    motion: 'particle',
    isConcept: true,
  },
  {
    name: '团队工程',
    type: 'SYSTEM / TEAM',
    description: '在版本、测试与协作中完成一套可靠系统。',
    pattern: 'system',
    image: teamEngineeringImage,
    imageAlt: '多条开发分支经过集成、测试后汇聚为可部署系统',
    imagePosition: '53% 52%',
    imageScale: 1.02,
    accent: '#2187ff',
    motion: 'merge',
    isConcept: true,
  },
]

export const seriesSites: readonly SeriesSite[] = createSiteRegistry(import.meta.env).filter(
  (site) => site.id !== 'se-universe',
)
